import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');
  const [displayComics, setDisplayComics] = useState<Comic[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    setDisplayComics(comics.filter((c) => c.category === activeSlug));
  }, [activeSlug]);

  const handleCategoryClick = (catSlug: string) => {
    setActiveSlug(catSlug);
    window.history.pushState(null, '', `/category/${catSlug}`);
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title={`${currentCategory.name}漫画 - jm漫画`}
        description={`jm漫画${currentCategory.name}分类：${currentCategory.description}。海量正版${currentCategory.name}漫画，高清画质，每日更新，免费在线阅读。`}
        keywords={`${currentCategory.name}漫画,jm漫画,在线漫画,${currentCategory.name}漫画推荐`}
        canonicalUrl={`https://jmcomic.com/category/${activeSlug}`}
      />

      <Navbar />

      <main className="pt-24 md:pt-28 pb-16 md:pb-20">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '分类浏览', to: '/category/rexue' },
            { label: currentCategory.name },
          ]}
        />

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
            {currentCategory.name}漫画
          </h1>
          <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2" role="tablist" aria-label="漫画分类切换">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                role="tab"
                aria-selected={activeSlug === cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
          {displayComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {displayComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-100">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-50">
                        {comic.rating}分
                      </span>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${
                        comic.status === '连载中' ? 'bg-accent-500 text-background-50' : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8">
                      <p className="text-xs text-background-50/80">{comic.author}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-foreground-500">{comic.views} 阅读</span>
                    <span className="text-[10px] text-foreground-400">更新至{comic.chapters[0]?.title}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-book-2-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </div>

        {displayComics.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">关于{currentCategory.name}漫画</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                jm漫画{currentCategory.name}频道汇集了全网最优质的{currentCategory.name}类漫画作品。{currentCategory.description}。
                我们与多家知名出版社和原创作者合作，确保每一部{currentCategory.name}漫画都是正版授权内容。
                无论你是{currentCategory.name}漫画的老粉丝还是新入坑的读者，都能在这里找到心仪的作品。
                每天都有新章节更新，支持高清画质阅读和离线下载功能，让你随时随地沉浸在{currentCategory.name}漫画的精彩世界中。
              </p>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}